<template>
  <div class="simple-image-container" :class="imageClass">
    <!-- Lazy loaded image -->
    <img
      v-show="isInView"
      :src="defaultSrc"
      :alt="alt"
      class="markdown-image gallery-trigger-image"
      @load="onImageLoad"
      @error="onImageError"
      @click="onImageClick"
    />

    <!-- Loading spinner when image is in view but not yet loaded -->
    <div v-if="isInView && !isLoaded" class="loading-spinner">
      <div class="spinner"></div>
    </div>
  </div>
</template>

<script>
import { createLazyLoadObserver } from "@/utils/imageOptimizer";

export default {
  name: "SimpleImage",
  props: {
    src: {
      type: String,
      required: true,
    },
    alt: {
      type: String,
      default: "",
    },
    imageClass: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      isInView: false,
      isLoaded: false,
      observer: null,
    };
  },
  computed: {
    // Extract base path and filename from src
    basePath() {
      const lastSlashIndex = this.src.lastIndexOf("/");
      return this.src.substring(0, lastSlashIndex);
    },
    filename() {
      const lastSlashIndex = this.src.lastIndexOf("/");
      const fullFilename = this.src.substring(lastSlashIndex + 1);
      const dotIndex = fullFilename.lastIndexOf(".");
      return dotIndex > 0 ? fullFilename.substring(0, dotIndex) : fullFilename;
    },
    // Use the highest quality WebP available for gallery
    webpSrc() {
      return `${this.basePath}/${this.filename}-xlarge.webp`;
    },
    // Default size for display - use medium for better mobile performance
    defaultSrc() {
      return `${this.basePath}/${this.filename}-medium.webp`;
    },
  },
  mounted() {
    this.setupIntersectionObserver();
    // Emit image data to parent for gallery collection
    console.log("SimpleImage: Registering image:", this.webpSrc);
    this.$emit("image-register", {
      src: this.webpSrc,
      alt: this.alt,
      thumbnailSrc: `${this.basePath}/${this.filename}-medium.webp`,
    });
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    setupIntersectionObserver() {
      this.observer = createLazyLoadObserver((entry) => {
        if (entry.isIntersecting) {
          this.isInView = true;
          this.observer.unobserve(entry.target);
        }
      });

      this.observer.observe(this.$el);
    },

    onImageLoad() {
      this.isLoaded = true;
      console.log("SimpleImage: Image loaded");
    },

    onImageError() {
      console.error("Failed to load image:", this.defaultSrc);
      this.isLoaded = true;
    },

    onImageClick() {
      console.log("SimpleImage: Image clicked:", this.webpSrc);
      // Emit click event with image data
      this.$emit("image-click", {
        src: this.webpSrc,
        alt: this.alt,
        thumbnailSrc: `${this.basePath}/${this.filename}-medium.webp`,
      });
    },
  },
};
</script>

<style scoped>
.simple-image-container {
  position: relative;
  display: inline-block;
  max-width: 100%;
  margin: 1rem 0;
}

.gallery-trigger-image {
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.gallery-trigger-image:hover {
  transform: scale(1.02);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.markdown-image {
  max-width: 100%;
  height: auto;
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.loading-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.spinner {
  width: 2rem;
  height: 2rem;
  border: 2px solid #e5e7eb;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Size modifiers for different image classes */
.simple-image-container.small {
  width: 50%;
  margin-left: auto;
  margin-right: auto;
}

.simple-image-container.medium {
  width: 75%;
  margin-left: auto;
  margin-right: auto;
}

.simple-image-container.full {
  width: 100%;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .simple-image-container {
    margin: 0.5rem 0;
  }

  .gallery-trigger-image:hover {
    transform: none;
  }

  /* Make small images normal size on mobile */
  .simple-image-container.small {
    width: 100%;
  }
}
</style>
